import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import DateTimePicker from '@/components/Custom/DateTimePicker';
import type { EventGenerateFormValues } from '@/components/Event/EventGenerate.types';
import { getEventGenerateScheduleSlotCount } from '@/components/Event/EventGenerate.utils';
import Box from '@/components/Unistyles/Box';
import Text from '@/components/Unistyles/Text';
import TextInput from '@/components/Unistyles/TextInput';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';

export default function EventGenerateSlots() {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const { push } = useRouter();
  const { control, setValue } = useFormContext<EventGenerateFormValues>();
  const rows = useWatch({ control, name: 'initialGames', defaultValue: [] });
  const selectedTeams = useWatch({
    control,
    name: 'selectedTeams',
    defaultValue: [],
  });
  const teamCount = selectedTeams.length;
  const slotCount = getEventGenerateScheduleSlotCount(teamCount);
  const hasFilledSlot = rows.some((row) => row.datetime.trim().length > 0);
  const hasInvalidSlot = rows.some((row) => {
    const datetime = row.datetime.trim();

    return datetime.length > 0 && !DateTime.fromISO(datetime).isValid;
  });
  const isValid = hasFilledSlot && !hasInvalidSlot;

  const setRows = (nextRows: EventGenerateFormValues['initialGames']) => {
    setValue('initialGames', nextRows, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('games', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onChangeRow = (
    rowId: string,
    field: 'datetime' | 'location',
    value: string,
  ) => {
    setRows(
      rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
  };

  const onPressContinue = () => {
    if (!isValid) {
      return;
    }

    push({
      pathname: '/event/[eventId]/generate/option',
      params: { eventId },
    });
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Game Weeks' }} />
      <WelcomeStepFrame
        title="Set the first week of games"
        subtitle={`Start with the first game date and weekly game slots. ${teamCount} teams create ${slotCount} slot${slotCount === 1 ? '' : 's'} per game week.`}
        footer={
          <Box space="sm" style={styles.footer}>
            <WelcomeCard>
              <Box
                direction="horizontal"
                alignItems="center"
                justifyContent="space-between"
                space="md"
              >
                <Text bold flexShrink={1}>
                  Number of Time Slots
                </Text>
                <Text
                  bold
                  size="lg"
                  selectable={false}
                  style={styles.countText}
                  testID="event-generate-schedule-slot-count"
                >
                  {rows.length}
                </Text>
              </Box>
            </WelcomeCard>
            <WelcomeActions>
              <WelcomePrimaryAction
                label="Continue"
                disabled={!isValid}
                onPress={onPressContinue}
              />
            </WelcomeActions>
          </Box>
        }
      >
        <WelcomeStepScrollContent>
          {rows.map((row, index) => (
            <WelcomeCard key={row.id}>
              <Text bold variant="typographySecondary">
                {`Slot ${index + 1}`}
              </Text>
              <DateTimePicker
                value={row.datetime}
                onChange={(value) =>
                  onChangeRow(row.id, 'datetime', value ?? '')
                }
                testID="EventGenerateOptionScheduleItemDateTime"
              />
              <TextInput
                enterKeyHint="done"
                placeholder="Location, optional"
                value={row.location ?? ''}
                onChangeText={(value) => onChangeRow(row.id, 'location', value)}
                testID="EventGenerateOptionScheduleItemLocation"
              />
            </WelcomeCard>
          ))}
          {hasInvalidSlot ? (
            <Text variant="error" center selectable>
              Use valid dates before continuing.
            </Text>
          ) : null}
        </WelcomeStepScrollContent>
      </WelcomeStepFrame>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  countText: {
    fontVariant: ['tabular-nums'],
  },
  footer: {
    alignSelf: 'center',
    maxWidth: theme.size(132),
    width: '100%',
  },
}));
